import { action } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import type { User } from "@shared/models/user"
import LiveQueryResource from "cc-frontend/helpers/live-query"
import type { Payload } from "cc-frontend/lib/actions/user/USER_UPDATE_ACCOUNT"
import userUpdateAccount from "cc-frontend/lib/actions/user/USER_UPDATE_ACCOUNT"
import type Router from "cc-frontend/router"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type Session from "cc-frontend/services/session"
import fade from "ember-animated/transitions/fade"
import { use } from "ember-could-get-used-to-this"
import { VALID_GOAL_ROLES } from "./onboarding/goals"

interface Sig {
  Args: {
    step: number
  }
}

export default class UserOnboardingComponent extends Component<Sig> {
  @service declare router: Router
  @service declare dispatcher: Dispatcher
  @service declare session: Session
  @service algolia

  @use existingPlanbooksQuery = new LiveQueryResource(() => [
    "planbooks_for_user",
    this.session.user.content.id,
  ])

  fadeTransition = fade

  @action
  updateField(field: Payload["field"], value) {
    let user = this.session.user.content
    if (user == null || user == undefined) return
    let val = value === "" ? null : value
    this.dispatcher.dispatchPreparedAction(
      userUpdateAccount({
        field,
        value: val,
        userId: user.id,
      })
    )
  }

  @action
  preventSubmit(e: Event) {
    e.preventDefault()
  }

  @action
  goToPlanbook(planbookId: string): void {
    this.endOnboarding()
    this.router.transitionTo("planbookv5.planner", planbookId)
  }

  @action
  goForward(): void {
    const nextStep = this.getNextStep(this.args.step)
    if (nextStep === "end") {
      this.endOnboarding()
      this.router.transitionTo("user.show", this.session.user.content.id)
    } else {
      // @ts-ignore
      this.router.transitionTo({ queryParams: { step: nextStep } })
    }
  }

  @action
  goBack(): void {
    const prevStep = this.getPrevStep(this.args.step)
    // @ts-ignore
    this.router.transitionTo({ queryParams: { step: prevStep } })
  }

  private getNextStep(step: number): number | "end" {
    switch (step) {
      case 2:
        if (this.shouldShowGoals()) return 3
        return this.getNextStep(3)
      case 3:
        if (!this.shouldCreatePlanbook()) return "end"
        if (this.shouldShowExistingPlanbooks()) return 4
        return 5
      case 6:
        return "end"
      default:
        return step + 1
    }
  }

  private getPrevStep(step: number): number {
    switch (step) {
      case 1:
        return 1
      case 4:
        if (this.shouldShowGoals()) return 3
        return 2
      case 5:
        if (this.shouldShowExistingPlanbooks()) return 4
        return this.getPrevStep(4)
      default:
        return step - 1
    }
  }

  private shouldShowGoals(): boolean {
    const userAttrs: User["attributes"] = this.session.user.content.attributes
    return VALID_GOAL_ROLES.includes(userAttrs.educationRole)
  }

  private shouldCreatePlanbook(): boolean {
    const userAttrs: User["attributes"] = this.session.user.content.attributes
    return userAttrs.educationRole !== "other" || userAttrs.workplace === "OTHER"
  }

  private shouldShowExistingPlanbooks(): boolean {
    return this.existingPlanbooksQuery.isFetching || this.existingPlanbooksQuery.results.length > 0
  }

  private endOnboarding(): void {
    this.dispatcher.dispatchPreparedAction(
      userUpdateAccount({
        field: "onboardingInProgress",
        value: false,
        userId: this.session.user.content.id,
      })
    )
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "User::Onboarding": typeof UserOnboardingComponent
  }
}
